import { FC, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  ActionMeta,
  components,
  DropdownIndicatorProps,
  InputActionMeta,
  MultiValue,
  SelectInstance,
  SingleValue,
} from 'react-select';
import { Option } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';

import { Icon } from '../../icon';
import { ClearIndicator } from '../clearIndicator/ClearIndicator';
import { useInputError } from '../hooks/useInputError';
import { InputWrapper, InputWrapperProps } from '../InputWrapper';
import { MultiValueRemove } from '../multiValueRemove/MultiValueRemove';

import '../typeAhead/typeAhead.scss';

export type SearchableSelectConfig = {
  customNoOptionsAvailable?: string;
  placeholder: string;
};

type SearchableSelectProps = InputWrapperProps & {
  closeMenuOnSelect?: boolean;
  filterOption?: (option: Option, inputValue: string) => boolean;
  isClearable?: boolean;
  isHidden?: boolean;
  isModal?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  onChange: (newValue: SingleValue<Option> | MultiValue<Option>, name: string) => void;
  options: Option[];
  searchableSelectConfig: SearchableSelectConfig;
  selectedOptions: readonly Option[];
};

const DropdownIndicator = ({ ...props }: DropdownIndicatorProps<Option, true>) => (
  <components.DropdownIndicator {...props}>
    {props.children}
    <Icon colorName={COLORS.zulu.$08} name={props.selectProps.menuIsOpen ? 'chevronUp' : 'chevronDown'} />
  </components.DropdownIndicator>
);

export const SearchableSelect: FC<SearchableSelectProps> = ({
  closeMenuOnSelect = false,
  filterOption,
  isClearable = true,
  isHidden,
  isModal,
  isMulti = true,
  isSearchable = true,
  label,
  onChange,
  options,
  searchableSelectConfig,
  selectedOptions,
  ...wrapperProps
}: SearchableSelectProps) => {
  const { t } = useTranslation();
  const { disabled, name, validation } = wrapperProps;
  const { setDirty, showError } = useInputError(validation);

  // Toggling visibility counts as user interaction, so the field has to surface its validation
  // error from here on. Comparing against the previous render (react.dev, "You Might Not Need an
  // Effect") keeps this in render: an effect would need `setDirty` in its deps, and `useInputError`
  // returns a fresh `setDirty` identity every render. `setDirty` only ever flips a flag to true, so
  // firing again on a later toggle is a no-op.
  const [prevIsHidden, setPrevIsHidden] = useState(isHidden);

  if (prevIsHidden !== isHidden) {
    setPrevIsHidden(isHidden);
    setDirty();
  }

  const selectRef = useRef<SelectInstance<Option, boolean>>(null);
  const inputValueRef = useRef('');

  // react-select clears the input via 'set-value' right before firing onChange, so we capture prevInputValue to know what the filter was at the moment of selection.
  const onInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    inputValueRef.current = actionMeta.action === 'set-value' ? actionMeta.prevInputValue : newValue;
  };

  const onChangeInternal = (newValue: SingleValue<Option> | MultiValue<Option>, actionMeta: ActionMeta<Option>) => {
    if (newValue) {
      onChange(newValue, name);
    } else {
      onChange([], name);
    }
    setDirty();
    // Only clear focus on text-filtered selections: after the filter clears, react-select would restore focus to the just-selected (now-selected) option and a 2nd Enter would deselect it. Without a text filter, leave the focus in place so arrow-nav / mouse clicks continue from the current position.
    if (actionMeta.action === 'select-option' && inputValueRef.current.length > 0) {
      selectRef.current?.setState({ focusedOption: null, focusedOptionId: null });
    }
  };

  // After clearing focus, react-select's Enter handler returns without preventDefault, so the keypress would bubble to a containing <form>.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !selectRef.current?.state.focusedOption) {
      event.preventDefault();
    }
  };

  const displayNoOptionsText = (inputValue: string) => {
    if (!inputValue) {
      return t('TYPEAHEAD.TYPE_TO_SEARCH');
    }

    return searchableSelectConfig?.customNoOptionsAvailable ?? t('TYPEAHEAD.NO_OPTIONS');
  };

  if (isHidden) {
    return null;
  }

  return (
    <InputWrapper {...wrapperProps} label={label} showError={showError}>
      <Select
        className={'type-ahead'}
        classNamePrefix="type-ahead"
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          ClearIndicator,
          DropdownIndicator,
          MultiValueRemove,
        }}
        filterOption={filterOption ?? undefined}
        hideSelectedOptions={false}
        isClearable={isClearable}
        isDisabled={disabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        maxMenuHeight={100}
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) => displayNoOptionsText(inputValue)}
        onChange={onChangeInternal}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        options={options}
        placeholder={searchableSelectConfig.placeholder}
        ref={selectRef}
        styles={
          isModal
            ? {
                menuPortal: defaultStyles => ({
                  ...defaultStyles,
                  zIndex: '10001',
                }),
              }
            : {}
        }
        value={selectedOptions}
      />
    </InputWrapper>
  );
};
